import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Layout from "../components/Layout";
import { ArrowLeft, BarChart3, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const WINDOWS = [
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
  { value: 365, label: "Last 365 days" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function AdminUsage() {
  const { user } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user?.is_admin) return;
    setLoading(true);
    api.get(`/admin/usage-stats?days=${days}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load usage stats"))
      .finally(() => setLoading(false));
  }, [days, user]);

  if (!user) return null;
  if (!user.is_admin) return <Navigate to="/" replace />;

  return (
    <Layout>
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-ink-700 hover:text-ink-900 mb-6">
        <ArrowLeft className="w-4 h-4" /> Back
      </Link>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-subnav">
        <Link to="/admin/usage" data-testid="admin-subnav-usage" className="min-h-[36px] px-3 rounded-full text-xs font-medium bg-ink-900 text-white inline-flex items-center gap-2">AI Usage</Link>
        <Link to="/admin/production" data-testid="admin-subnav-production" className="min-h-[36px] px-3 rounded-full text-xs font-medium bg-white border border-paper-200 text-ink-700 hover:bg-paper-100 inline-flex items-center gap-2">Production</Link>
        <Link to="/admin/billing" data-testid="admin-subnav-billing" className="min-h-[36px] px-3 rounded-full text-xs font-medium bg-white border border-paper-200 text-ink-700 hover:bg-paper-100 inline-flex items-center gap-2">Billing & Verification</Link>
        <Link to="/admin/feedback" data-testid="admin-subnav-feedback" className="min-h-[36px] px-3 rounded-full text-xs font-medium bg-white border border-paper-200 text-ink-700 hover:bg-paper-100 inline-flex items-center gap-2">Feedback</Link>
      </div>

      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="inline-flex items-center gap-2 mb-2 px-3 py-1 rounded-full bg-terracotta-500/10 text-terracotta-600 text-xs uppercase tracking-[0.14em]">
            <BarChart3 className="w-3.5 h-3.5" /> Admin only
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-ink-900 mb-1">AI usage</h1>
          <p className="text-ink-700">Per-therapist breakdown of AI calls — use this when sizing free-tier limits.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            data-testid="usage-window-select"
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value))}
            className="min-h-[44px] px-4 rounded-xl bg-white border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none"
          >
            {WINDOWS.map((w) => (
              <option key={w.value} value={w.value}>{w.label}</option>
            ))}
          </select>
        </div>
      </div>

      {(() => {
        if (loading) return <div className="text-ink-500">Loading…</div>;
        if (!data) return null;
        return (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <Stat label="Therapists active" value={data.totals.users_with_activity} />
              <Stat label="Total AI calls" value={data.totals.total_calls} />
              <Stat label="Total response chars" value={data.totals.total_response_chars.toLocaleString()} />
            </div>

            <div className="bg-white border border-paper-200 rounded-3xl overflow-hidden" data-testid="usage-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-paper-50 border-b border-paper-200 text-left">
                    <tr>
                      <Th>Therapist</Th>
                      <Th>Tier</Th>
                      <Th align="right">Total</Th>
                      <Th align="right">Posture</Th>
                      <Th align="right">SOAP</Th>
                      <Th align="right">Protocol</Th>
                      <Th align="right">Chars</Th>
                      <Th>Last call</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.per_user.length === 0 ? (
                      <tr><td colSpan={8} className="px-4 py-8 text-center text-ink-500">No AI activity in this window.</td></tr>
                    ) : (
                      data.per_user.map((u) => (
                        <tr key={u.user_id} className="border-b border-paper-200 last:border-0 hover:bg-paper-50/60" data-testid={`usage-row-${u.user_id}`}>
                          <Td>
                            <div className="font-medium text-ink-900">{u.name}</div>
                            <div className="text-xs text-ink-500">{u.email}</div>
                          </Td>
                          <Td><TierBadge tier={u.tier} /></Td>
                          <Td align="right" mono>{u.total_calls}</Td>
                          <Td align="right" mono>{u.posture_analyze}</Td>
                          <Td align="right" mono>{u.soap_suggest}</Td>
                          <Td align="right" mono>{u.protocols_suggest}</Td>
                          <Td align="right" mono className="text-ink-500">{u.total_response_chars.toLocaleString()}</Td>
                          <Td className="text-ink-500">{fmtDate(u.last_call_at)}</Td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <p className="text-xs text-ink-500 mt-4">
              Tip: a ratio of ~10–15k response chars per session is typical (≈3 AI calls). Multiply by your Claude pricing to estimate cost-per-active-therapist.
            </p>
          </>
        );
      })()}
    </Layout>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white border border-paper-200 rounded-2xl p-5">
      <div className="label-overline mb-2">{label}</div>
      <div className="font-heading text-3xl text-ink-900">{value}</div>
    </div>
  );
}

function Th({ children, align = "left" }) {
  return <th className={`px-4 py-3 label-overline text-${align}`}>{children}</th>;
}

function Td({ children, align = "left", mono = false, className = "" }) {
  const cls = `${mono ? "font-mono tabular-nums" : ""} ${className}`.trim();
  return <td className={`px-4 py-3 text-${align} ${cls}`}>{children}</td>;
}

function TierBadge({ tier }) {
  const map = {
    free: "bg-paper-100 text-ink-700",
    pro: "bg-sage-500/10 text-sage-700",
    clinic: "bg-terracotta-500/10 text-terracotta-600",
  };
  return <span className={`px-2 py-1 rounded-full text-xs ${map[tier] || map.free}`}>{tier}</span>;
}
